import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Institution } from 'domain/entities/representative'
import { FilterOperator } from 'domain/models/filterOperator'
import { FilterValueType } from 'domain/models/filterValueType'
import { Query } from 'domain/models/query'
import {
  deleteSelectedRepresentativeInstitution,
  representativeState,
} from 'presentation/redux/slices/representativeSlice'
import {
  useGetInstitutionsByRepresentative,
  useSaveInstitutionInSessionStorage,
} from 'presentation/redux/hooks/representative'
import { authUserState } from 'presentation/redux/slices/authUserSlice'
import { useNotifications } from 'presentation/redux/hooks/notifications'
import { urlRoutes } from 'presentation/routes/config/urlRoutes/urlRoutes'

export const useHeader = () => {
  const [selectedItemMenu, setSelectedItemMenu] = useState<Institution | null>(null)
  const [anchorStudentEl, setAnchorStudentEl] = useState<null | HTMLElement>(null)
  const [anchorNotificationsEl, setAnchorNotificationsEl] = useState<null | HTMLElement>(null)
  const [anchorUserEl, setAnchorUserEl] = useState<null | HTMLElement>(null)
  const [anchorSignInEl, setAnchorSignInEl] = useState<null | HTMLElement>(null)
  const [isOpenBackdrop, setIsOpenBackdrop] = useState<boolean>(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { logged, user } = useSelector(authUserState)
  const { institutions, selectedInstitution } = useSelector(representativeState)
  const {
    isLoading: isLoadingGetInstitutionsByRepresentative,
    error: errorGetInstitutionsByRepresentative,
    getInstitutionsByRepresentative,
  } = useGetInstitutionsByRepresentative()
  const { saveInstitutionInSessionStorage } = useSaveInstitutionInSessionStorage()
  const { getNotifications, isLoading, error, notifications } = useNotifications()

  const isOpenStudentMenu = !!anchorStudentEl
  const isOpenNotificationsMenu = !!anchorNotificationsEl
  const isOpenUserMenu = !!anchorUserEl
  const isOpenSignInMenu = !!anchorSignInEl

  const handleSaveSelectedInstitutionInSessionStorage = useCallback(
    (institution: Institution) => {
      saveInstitutionInSessionStorage(institution)
      setSelectedItemMenu(institution)
    },
    [saveInstitutionInSessionStorage],
  )

  useEffect(() => {
    if (selectedInstitution?.promotionId) {
      handleSaveSelectedInstitutionInSessionStorage(selectedInstitution)
    }
  }, [selectedInstitution?.promotionId, handleSaveSelectedInstitutionInSessionStorage])

  useEffect(() => {
    if (logged) {
      const handleGetInstitutionsByRepresentative = async () => {
        await getInstitutionsByRepresentative()
      }
      handleGetInstitutionsByRepresentative()
    }
  }, [])

  const handleGetNotifications = useCallback(async () => {
    const filters = [
      `readed__${FilterOperator.equal}__${FilterValueType.bool}(${false})__AND`,
      `representativeId__${FilterOperator.equal}__${logged}__AND`,
    ]
    const data: Query = {
      filters,
    }
    await getNotifications(data)
  }, [getNotifications, logged])

  useEffect(() => {
    if (logged) {
      handleGetNotifications()
    }
  }, [])

  useEffect(() => {
    return () => {
      dispatch(deleteSelectedRepresentativeInstitution())
    }
  }, [dispatch])
  const handleCloseStudentMenu = () => {
    setAnchorStudentEl(null)
  }
  const handleCloseUserMenu = () => {
    setAnchorUserEl(null)
  }
  const handleCloseNotificationsMenu = () => {
    setAnchorNotificationsEl(null)
  }
  const handleCloseSignInMenu = () => {
    setAnchorSignInEl(null)
  }
  const handleClickListSignInItemMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorSignInEl(isOpenSignInMenu ? null : event.currentTarget)
  }
  const handleClickItemMenu = async (event: MouseEvent<HTMLElement>, item: Institution) => {
    saveInstitutionInSessionStorage(item)
    setSelectedItemMenu(item)
    handleCloseStudentMenu()
  }

  const handleClickListStudentItemMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorStudentEl(isOpenStudentMenu ? null : event.currentTarget)
    if (anchorNotificationsEl) {
      setAnchorNotificationsEl(null)
    }
    if (anchorUserEl) {
      setAnchorUserEl(null)
    }
  }

  const handleClickListNotificationsItemMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorNotificationsEl(isOpenNotificationsMenu ? null : event.currentTarget)
    if (anchorStudentEl) {
      setAnchorStudentEl(null)
    }
    if (anchorUserEl) {
      setAnchorUserEl(null)
    }
  }

  const handleClickListUserItemMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorUserEl(isOpenUserMenu ? null : event.currentTarget)
    if (anchorNotificationsEl) {
      setAnchorNotificationsEl(null)
    }
    if (anchorNotificationsEl) {
      setAnchorNotificationsEl(null)
    }
  }

  const handleOpenBackdrop = () => {
    setIsOpenBackdrop(!isOpenBackdrop)
  }
  const handleCloseBackdrop = () => {
    setIsOpenBackdrop(false)
    handleCloseNotificationsMenu()
  }

  const idsArray = {
    ids: notifications.map((item: any) => item.notificationId),
  }

  const handleClickMainMenu = () => {
    navigate(urlRoutes.private.goalAssignment.urlTo)
  }

  return {
    isLoadingGetInstitutionsByRepresentative,
    errorGetInstitutionsByRepresentative,
    handleClickListStudentItemMenu,
    handleClickListUserItemMenu,
    handleClickListNotificationsItemMenu,
    handleClickItemMenu,
    handleClickListSignInItemMenu,
    handleCloseSignInMenu,
    handleCloseUserMenu,
    handleCloseNotificationsMenu,
    handleCloseStudentMenu,
    selectedItemMenu,
    isOpenStudentMenu,
    isOpenSignInMenu,
    isOpenNotificationsMenu,
    isOpenUserMenu,
    anchorNotificationsEl,
    anchorStudentEl,
    anchorSignInEl,
    anchorUserEl,
    user,
    logged,
    institutions,
    handleClickMainMenu,
    idsArray,
    handleCloseBackdrop,
    handleOpenBackdrop,
    isLoading,
    error,
    notifications,
    handleGetNotifications,
    isOpenBackdrop,
  }
}
